import React, {useEffect, useRef, useState} from "react";
import AppContainer from "../../common/app_container";
import {useAudioTrackAPI} from "@src/api/audio_track_api";
import {useFileAPI} from "@src/api/file_api";
import {useOrganizationAPI} from "@src/api/organization.api";
import {AudioTrack, AudioType} from "@src/model/audio_track";
import {Organization} from "@src/model/organization";
import {useNavigate, useParams} from "react-router-dom";
import {SubmitHandler, useForm} from "react-hook-form";
import Alert, {AlertProps} from "@src/common/alert";
import BackButton from "@src/common/components/back-button";

interface AudioTrackEditProps {
}

const AudioTrackEdit: React.FC<AudioTrackEditProps> = () => {
    const {id} = useParams();
    const audioTrackAPI = useAudioTrackAPI();
    const fileAPI = useFileAPI();
    const organizationAPI = useOrganizationAPI();
    const navigate = useNavigate();
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isBackfilling, setIsBackfilling] = useState(false);

    // Organizations state
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrgs, setLoadingOrgs] = useState(true);

    // File upload state
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [previewUploadProgress, setPreviewUploadProgress] = useState<number>(0);
    const [audioUploadProgress, setAudioUploadProgress] = useState<number>(0);
    const [isUploadingPreview, setIsUploadingPreview] = useState(false);
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);

    const previewFileInputRef = useRef<HTMLInputElement>(null);
    const audioFileInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: {errors},
    } = useForm<AudioTrack>();

    const currentPreviewUrl = watch("preview_url");
    const currentFileUrl = watch("file_url");

    const uploadFile = async (
        file: File,
        audioTrackId: string,
        onProgress: (progress: number) => void
    ): Promise<{ permalink: string; fileId: string }> => {
        const uploadedFiles = await fileAPI.uploadFile(
            {
                files: [file],
                is_public: true,
                object_type: "AUDIO_TRACK",
                object_id: audioTrackId,
            },
            (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percent);
                }
            }
        );

        if (uploadedFiles && uploadedFiles.length > 0 && uploadedFiles[0].permalink) {
            return {
                permalink: uploadedFiles[0].permalink,
                fileId: uploadedFiles[0].file_id || uploadedFiles[0].id || "",
            };
        }
        throw new Error("Failed to get uploaded file URL");
    };

    const onSubmit: SubmitHandler<AudioTrack> = async (data) => {
        setIsSaving(true);

        // Convert is_public to boolean if it's a string
        if (typeof data.is_public === "string") {
            data.is_public = data.is_public === "true";
        }

        try {
            let trackId = id;

            if (id) {
                // Existing track: update first, then upload files, then update with file URLs
                await audioTrackAPI.updateAudioTrack(id, data);
                trackId = id;

                let updatedPreviewUrl = data.preview_url;
                let updatedFileUrl = data.file_url;
                let updatedFileId = data.file_id;

                if (previewFile) {
                    setIsUploadingPreview(true);
                    try {
                        const result = await uploadFile(previewFile, trackId, setPreviewUploadProgress);
                        updatedPreviewUrl = result.permalink;
                    } finally {
                        setIsUploadingPreview(false);
                    }
                }

                if (audioFile) {
                    setIsUploadingAudio(true);
                    try {
                        const result = await uploadFile(audioFile, trackId, setAudioUploadProgress);
                        updatedFileUrl = result.permalink;
                        updatedFileId = result.fileId;
                    } finally {
                        setIsUploadingAudio(false);
                    }
                }

                if (previewFile || audioFile) {
                    await audioTrackAPI.updateAudioTrack(trackId, {
                        ...data,
                        preview_url: updatedPreviewUrl,
                        file_url: updatedFileUrl,
                        file_id: updatedFileId,
                    });
                }
            } else {
                // New track: create first to get an ID, then upload files, then update with file URLs
                let updatedPreviewUrl = data.preview_url;
                let updatedFileUrl = data.file_url;
                let updatedFileId = data.file_id;

                // Create the track first to get an ID for file association
                const savedTrack = await audioTrackAPI.createAudioTrack(data);
                trackId = savedTrack.id;

                if (!trackId) {
                    throw new Error("Failed to get audio track ID");
                }

                if (previewFile) {
                    setIsUploadingPreview(true);
                    try {
                        const result = await uploadFile(previewFile, trackId, setPreviewUploadProgress);
                        updatedPreviewUrl = result.permalink;
                    } finally {
                        setIsUploadingPreview(false);
                    }
                }

                if (audioFile) {
                    setIsUploadingAudio(true);
                    try {
                        const result = await uploadFile(audioFile, trackId, setAudioUploadProgress);
                        updatedFileUrl = result.permalink;
                        updatedFileId = result.fileId;
                    } finally {
                        setIsUploadingAudio(false);
                    }
                }

                // For new tracks, always update after file upload to set file references
                if (previewFile || audioFile) {
                    await audioTrackAPI.updateAudioTrack(trackId, {
                        ...savedTrack,
                        preview_url: updatedPreviewUrl,
                        file_url: updatedFileUrl,
                        file_id: updatedFileId,
                    });
                }
            }

            setIsSaving(false);
            setStatus({type: "success", message: "Audio track saved successfully"});
            setTimeout(() => {
                navigate("/audio-tracks");
            }, 2000);
        } catch (err: any) {
            setIsSaving(false);
            setIsUploadingPreview(false);
            setIsUploadingAudio(false);
            setStatus({type: "error", message: "Error saving audio track: " + err.message});
        }
    };

    const handlePreviewFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setPreviewFile(e.target.files[0]);
            setPreviewUploadProgress(0);
        }
    };

    const handleAudioFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setAudioFile(e.target.files[0]);
            setAudioUploadProgress(0);
        }
    };

    const clearPreviewFile = () => {
        setPreviewFile(null);
        setPreviewUploadProgress(0);
        if (previewFileInputRef.current) {
            previewFileInputRef.current.value = "";
        }
    };

    const clearAudioFile = () => {
        setAudioFile(null);
        setAudioUploadProgress(0);
        if (audioFileInputRef.current) {
            audioFileInputRef.current.value = "";
        }
    };

    // Load organizations on mount
    useEffect(() => {
        organizationAPI
            .getAllOrganizations()
            .then((orgs) => {
                setOrganizations(orgs);
                setLoadingOrgs(false);
            })
            .catch((err) => {
                console.error("Error loading organizations:", err);
                setLoadingOrgs(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (id) {
            audioTrackAPI
                .getAudioTrackById(id)
                .then((response) => {
                    reset(response);
                })
                .catch((err) => {
                    setStatus({type: "error", message: "Error loading audio track: " + err.message});
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleBackfillBeats = async () => {
        setIsBackfilling(true);
        try {
            const result = await audioTrackAPI.backfillBeatData();
            setStatus({type: "success", message: `Beat detection submitted for ${result.submitted} audio track(s)`});
        } catch (err: any) {
            setStatus({type: "error", message: "Failed to backfill beat data: " + err.message});
        } finally {
            setIsBackfilling(false);
        }
    };

    const isUploading = isUploadingPreview || isUploadingAudio;

    return (
        <AppContainer>
            <div className="container">
                <BackButton variant="ghost" onClick={() => navigate("/audio-tracks")}/>
                <div className="flex justify-between">
                    <h1 className="text-3xl">{id ? "Edit Audio Track" : "Add Audio Track"}</h1>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                            onClick={handleBackfillBeats}
                            disabled={isBackfilling}
                        >
                            {isBackfilling ? "Submitting..." : "Backfill Beat Data"}
                        </button>
                        <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                            type="submit"
                            form="audio_track_form"
                            disabled={isSaving || isUploading}
                        >
                            {isUploading ? "Uploading..." : isSaving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
                {status && (
                    <div className="mt-4">
                        <Alert {...status} onClose={() => setStatus(null)}/>
                    </div>
                )}
                <form onSubmit={handleSubmit(onSubmit)} id="audio_track_form">
                    <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div className="sm:col-span-4">
                            <label htmlFor="name"
                                   className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Name *
                            </label>
                            <div className="mt-2">
                                <div
                                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <input
                                        {...register("name", {required: "Name is required"})}
                                        type="text"
                                        placeholder="Audio Track Name"
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                    />
                                </div>
                                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <label htmlFor="audio_type"
                                   className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Audio Type *
                            </label>
                            <div className="mt-2">
                                <div
                                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <select
                                        {...register("audio_type", {required: "Audio type is required"})}
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                    >
                                        <option value="">-- Select Audio Type --</option>
                                        {Object.values(AudioType).map((type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.audio_type &&
                                    <p className="text-red-500 text-sm mt-1">{errors.audio_type.message}</p>}
                            </div>
                        </div>

                        <div className="sm:col-span-6">
                            <label htmlFor="description"
                                   className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Description
                            </label>
                            <div className="mt-2">
                <textarea
                    {...register("description")}
                    rows={3}
                    placeholder="Description of the audio track"
                    className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <label htmlFor="tags"
                                   className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Tags
                            </label>
                            <div className="mt-2">
                                <div
                                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <input
                                        {...register("tags")}
                                        type="text"
                                        placeholder="comma, separated, tags"
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Preview File Upload */}
                        <div className="sm:col-span-6">
                            <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Preview Audio File
                            </label>
                            <div className="mt-2 space-y-2">
                                {currentPreviewUrl && !previewFile && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <span>Current:</span>
                                        <a href={currentPreviewUrl} target="_blank" rel="noopener noreferrer"
                                           className="text-blue-500 hover:underline truncate max-w-md">
                                            {currentPreviewUrl}
                                        </a>
                                    </div>
                                )}
                                <div className="flex items-center gap-4">
                                    <input
                                        ref={previewFileInputRef}
                                        type="file"
                                        accept="audio/*"
                                        onChange={handlePreviewFileChange}
                                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                                    />
                                    {previewFile && (
                                        <button
                                            type="button"
                                            onClick={clearPreviewFile}
                                            className="text-red-500 hover:text-red-700 text-sm"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                {previewFile && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Selected: {previewFile.name} ({(previewFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </div>
                                )}
                                {isUploadingPreview && (
                                    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                        <div
                                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                            style={{width: `${previewUploadProgress}%`}}
                                        ></div>
                                        <span className="text-xs text-gray-500">{previewUploadProgress}%</span>
                                    </div>
                                )}
                            </div>
                            {/* Hidden input for manual URL entry */}
                            <input type="hidden" {...register("preview_url")} />
                        </div>

                        {/* Audio File Upload */}
                        <div className="sm:col-span-6">
                            <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Full Audio File
                            </label>
                            <div className="mt-2 space-y-2">
                                {currentFileUrl && !audioFile && (
                                    <>
                                        <div
                                            className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                            <span>Current:</span>
                                            <a href={currentFileUrl} target="_blank" rel="noopener noreferrer"
                                               className="text-blue-500 hover:underline truncate max-w-md">
                                                {currentFileUrl}
                                            </a>
                                        </div>
                                        <div className="mt-2">
                                            <audio
                                                controls
                                                className="w-full max-w-md"
                                                src={currentFileUrl}
                                            >
                                                Your browser does not support the audio element.
                                            </audio>
                                        </div>
                                    </>
                                )}
                                <div className="flex items-center gap-4">
                                    <input
                                        ref={audioFileInputRef}
                                        type="file"
                                        accept="audio/*"
                                        onChange={handleAudioFileChange}
                                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                                    />
                                    {audioFile && (
                                        <button
                                            type="button"
                                            onClick={clearAudioFile}
                                            className="text-red-500 hover:text-red-700 text-sm"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                {audioFile && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Selected: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </div>
                                )}
                                {isUploadingAudio && (
                                    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                        <div
                                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                                            style={{width: `${audioUploadProgress}%`}}
                                        ></div>
                                        <span className="text-xs text-gray-500">{audioUploadProgress}%</span>
                                    </div>
                                )}
                            </div>
                            {/* Hidden input for manual URL entry */}
                            <input type="hidden" {...register("file_url")} />
                            <input type="hidden" {...register("file_id")} />
                        </div>

                        <div className="sm:col-span-4">
                            <label htmlFor="is_public"
                                   className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Is Public *
                            </label>
                            <div className="mt-2">
                                <div
                                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <select
                                        {...register("is_public", {required: "Public status is required"})}
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                    >
                                        <option value="">-- Select Public Status --</option>
                                        <option value="true">Yes (Public)</option>
                                        <option value="false">No (Private)</option>
                                    </select>
                                </div>
                                {errors.is_public &&
                                    <p className="text-red-500 text-sm mt-1">{errors.is_public.message}</p>}
                            </div>
                        </div>

                        <div className="sm:col-span-4">
                            <label htmlFor="organization_id"
                                   className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                                Organization
                            </label>
                            <div className="mt-2">
                                <div
                                    className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                    <select
                                        {...register("organization_id")}
                                        className="block flex-1 border-0 bg-transparent py-1.5 pl-3 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                                        disabled={loadingOrgs}
                                    >
                                        <option value="">-- None (Public Track) --</option>
                                        {loadingOrgs ? (
                                            <option disabled>Loading organizations...</option>
                                        ) : (
                                            organizations.map((org) => (
                                                <option key={org.id} value={org.id}>
                                                    {org.name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Select an organization to make this track private to that organization
                                </p>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </AppContainer>
    );
};

export default AudioTrackEdit;
